import { FormHelperText, Container, Typography, Autocomplete } from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState, useRef } from 'react';
import { timezones } from '../../constants/timezones';
import { useField } from 'formik';
import TextFieldWrapper from '../../common/TextFieldWrapper/TextFieldWrapper';
import { isOnlyDateBefore } from '../../utils/dateUtils';

interface AllocateDatesProps {
    formikStartDate: string;
    formikEndDate: string;
    formikTimezoneValue?: string;
    title?: string;
    description?: string;
    datetime?: boolean;
    formikDateRange?: string;
    descriptionVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'body1' | 'body2' | 'caption';
    titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'body1' | 'body2' | 'caption';
    datesClass?: string;
    containerSpacing?: number;
    startEndDateColumns?: number;
    timeZoneColumns?: number;
    useTitles?: boolean;
    showTimeZone?: boolean;
    isEdit?: boolean;
    disabled?: boolean;
    minStartDate?: string | number;
    maxEndDate?: string | number;
    isNewPrizeState?: boolean;
    isUpdatePrizeState?: boolean;
}

const dateTimeFormat = 'YYYY-MM-DDTHH:mmZ';

function geObjectValue(city: string) {
    const res = Object.values(timezones).find((v) => {
        if (v.includes(city)) {
            return v;
        }
        return undefined;
    });
    return res;
}

let currentTimestamp = Date.now();

const userTimeZone = moment.tz.guess();
const zoneValue = geObjectValue(userTimeZone.split('/')[1]);
let defaultTimeZone: string | undefined;
if (userTimeZone) {
    defaultTimeZone = timezones[userTimeZone] ? timezones[userTimeZone] : zoneValue;
}
if (!defaultTimeZone) {
    defaultTimeZone = timezones['Europe/London'];
}

function AllocateDates({
    title,
    description,
    datetime,
    formikDateRange,
    formikStartDate,
    formikEndDate,
    titleVariant,
    descriptionVariant,
    formikTimezoneValue,
    datesClass,
    containerSpacing = 1,
    startEndDateColumns = 4,
    timeZoneColumns = 4,
    useTitles,
    showTimeZone = true,
    isEdit,
    disabled = false,
    minStartDate, // earliest date enabled in the calendar
    maxEndDate, // latest date enabled in the calendar
    isNewPrizeState,
    isUpdatePrizeState // Edit mode only. Forces the component to use the EXISTING START DATE from the database
                                   // and PREVENTS AUTO-SETTING the start date input to the current time.
}: AllocateDatesProps): any {
    const [startDateField, startDateFieldMeta, startDateFieldHelpers] = useField(formikStartDate);
    const [endDateField, endDateFieldMeta, endDateFieldHelpers] = useField(formikEndDate);
    const [tzField, , tzFieldHelpers] = useField(formikTimezoneValue || 'timeZoneField');
    const { setValue: tzSetValue } = tzFieldHelpers;
    const [minDate, setMinDate] = useState(moment().utc());
    const [selectedTimeZone, setSelectedTimeZone] = useState();
    const { setValue: setStartFieldValue } = startDateFieldHelpers;
    const [validationErrorStart, setValidationErrorStart] = useState<string | null>(null);
    const [validationErrorEnd, setValidationErrorEnd] = useState<string | null>(null);
    
    const calledOnce = useRef(false);
    const startDateTimeStamp = moment(startDateField.value).unix() * 1000;
    let timeStampCompare = (timeStamp1: number, timeStamp2: number) => {
        return timeStamp1 < timeStamp2;
    };

    const IGNORED_ERRORS = new Set([
        'minDate',
        'disablePast'
    ]);

    const getAndBypassDatePickerError = (ignoreError: boolean, validationError: string | null): boolean | undefined  =>  {
        // if ignoreError is false or undfiend DesktopDateTimePicker print his own error message
        if (ignoreError !== true) {
            return undefined;
        }

        return validationError !== null && !IGNORED_ERRORS.has(validationError);
    }

    useEffect(() => {
        if (!calledOnce.current && startDateField.value !== '') {
            const isStartDateInThePast = timeStampCompare(startDateTimeStamp, currentTimestamp);
            if (isStartDateInThePast && !isEdit && !isUpdatePrizeState) {
               const currentMoment = moment(currentTimestamp).toDate().getTime(); 
               setStartFieldValue(currentMoment);
            }

            const initialDate = datetime ? moment(startDateField.value) : moment(startDateField.value).utc(true);
            initialDate ? setMinDate(initialDate) : setMinDate(minDate);
            calledOnce.current = true;
        }
        if (formikTimezoneValue && (tzField.value === '' || !tzField.value)) {
            tzSetValue(defaultTimeZone);
        } else if (Object.keys(timezones).includes(tzField.value)) {
            tzSetValue(timezones[tzField.value]);
        }
    }, [
        datetime,
        formikTimezoneValue,
        tzField.value,
        minDate,
        setMinDate,
        tzSetValue,
        startDateField.value,
        setStartFieldValue,
        startDateTimeStamp,
        isEdit,
        isUpdatePrizeState
    ]);

    const handleTimeZone = (timezoneOption: string) => {
        let timezone;
        timezone = Object.keys(timezones).find((key) => timezones[key] === timezoneOption);
        if (!timezone) {
            timezone = 'Europe/London';
        }
        setSelectedTimeZone(timezone);

        tzFieldHelpers.setValue(timezoneOption);
    };

    const preventKeyboardInput = (e: any) => {
        e.preventDefault();
    };

    const getTimeZone = () => {
        return selectedTimeZone || Object.keys(timezones).find((key) => timezones[key] === tzField.value);;
    }

    const endDateTimestamp = moment(endDateField.value).unix() * 1000;

    return (
        <Container disableGutters>
            <Typography variant={titleVariant ? titleVariant : 'h3'} gutterBottom>
                {title}
            </Typography>
            <Typography variant={descriptionVariant ? descriptionVariant : 'body1'} sx={{ marginBottom: 2 }}>
                {description}
            </Typography>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale='en-US'
                localeText={{
                    start: 'Start Date',
                    end: 'End Date',
                }}>
                {datetime && (
                    <Grid container spacing={containerSpacing} className={datesClass}>
                        <Grid item xs={startEndDateColumns}>
                            {useTitles && <Typography variant='body1'> Start Date</Typography>}
                            <DesktopDateTimePicker
                                data-testid={'datePicker'}
                                label={!useTitles && 'Start Date'}
                                minDate={minStartDate ? moment(minStartDate) : minDate}
                                referenceDate={isUpdatePrizeState ? moment(currentTimestamp) : undefined}
                                maxDate={isUpdatePrizeState ? moment(maxEndDate) : moment(endDateField.value)}
                                timeSteps={{minutes: 1}}
                                ampm={false}
                                shouldDisableTime={() => {
                                    return isUpdatePrizeState && isOnlyDateBefore(startDateTimeStamp, currentTimestamp);
                                }}
                                disabled={
                                    disabled || (isEdit && timeStampCompare(startDateTimeStamp, currentTimestamp))
                                }
                                disablePast={true}
                                value={moment(startDateField.value)}
                                onChange={(date) => startDateFieldHelpers.setValue(date?.format(dateTimeFormat))}
                                onError={(reason) => setValidationErrorStart(reason)}
                                slotProps={{
                                    field: { readOnly: true },
                                    textField: {
                                        onKeyDown: preventKeyboardInput,
                                        className: 'input-text',
                                        error: getAndBypassDatePickerError(
                                            (isUpdatePrizeState || isNewPrizeState) && timeStampCompare(startDateTimeStamp, currentTimestamp), validationErrorStart
                                        )
                                    },
                                }}
                                timezone={getTimeZone()}
                            />
                            <FormHelperText error={true}>{startDateFieldMeta.error}</FormHelperText>
                        </Grid>
                        <Grid item xs={startEndDateColumns}>
                            {useTitles && <Typography variant='body1'> End Date</Typography>}
                            <DesktopDateTimePicker
                                disabled={disabled || (isEdit && timeStampCompare(endDateTimestamp, currentTimestamp))}
                                label={!useTitles && 'End Date'}
                                minDate={isUpdatePrizeState ? moment(currentTimestamp) : moment(startDateField.value)}
                                referenceDate={isUpdatePrizeState ? moment(currentTimestamp) : undefined}
                                ampm={false}
                                shouldDisableTime={() => {
                                    return isUpdatePrizeState && isOnlyDateBefore(endDateTimestamp, currentTimestamp);
                                }}
                                maxDate={maxEndDate ? moment(maxEndDate) : undefined}
                                timeSteps={{minutes: 1}}
                                value={moment(endDateField.value)}
                                onChange={(date) => endDateFieldHelpers.setValue(date?.format(dateTimeFormat))}
                                onError={(reason) => setValidationErrorEnd(reason)}
                                slotProps={{
                                    field: { readOnly: true },
                                    textField: {
                                        onKeyDown: preventKeyboardInput,
                                        className: 'input-text',
                                        error: getAndBypassDatePickerError(isUpdatePrizeState && timeStampCompare(endDateTimestamp, currentTimestamp), validationErrorEnd)
                                    },
                                }}
                                timezone={getTimeZone()}
                            />
                            <FormHelperText error={true}>{endDateFieldMeta.error}</FormHelperText>
                        </Grid>
                        {showTimeZone && (
                            <Grid item xs={timeZoneColumns}>
                                {useTitles && <Typography variant='body1'>Time Zone</Typography>}
                                <Autocomplete
                                    disabled={disabled}
                                    options={Object.values(timezones)}
                                    getOptionLabel={(label) => label}
                                    id='timezone'
                                    className='input-text'
                                    value={tzField.value ? timezones[tzField.value] || tzField.value : defaultTimeZone}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    onChange={(event, value) => {
                                        handleTimeZone(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextFieldWrapper
                                            {...params}
                                            label={!useTitles && 'Time Zone'}
                                            variant='outlined'
                                            onKeyDown={(e) => {
                                                e.preventDefault();
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </LocalizationProvider>
        </Container>
    );
}

export { AllocateDates };
